// SEO landing pages — one entry per URL. Template renders these in Consultant.jsx.
export const consultantPages = {
  "ai-growth-marketing-consultant": {
    seoTitle: "AI Growth Marketing Consultant · Shivani Ranawat",
    seoDesc: "Hire an AI Growth Marketing Consultant. I help startups and digital businesses grow faster using AI automation, performance marketing, CRO and data-driven systems. 7+ years, 50+ brands, 5–7x ROAS.",
    eyebrow: "Consulting · AI Growth",
    h1: "AI Growth Marketing Consultant",
    lead: "I help startups and digital businesses grow faster using AI automation, performance marketing, CRO and data-driven growth systems — turning marketing from a cost centre into a compounding revenue engine.",
    problems: [
      "You've invested in ads but ROAS has plateaued",
      "AI tools live in tabs, not in workflows",
      "Reporting eats more time than decisions",
      "Attribution is unreliable across platforms",
    ],
    approach: [
      { t: "Diagnose", d: "9-point audit across traffic, tracking, funnel, LP, offer, retention." },
      { t: "Rebuild", d: "Fix leaks in acquisition + conversion. Ship a working funnel in 2 weeks." },
      { t: "Automate", d: "Deploy AI agents for anomaly detection, budget shifts and daily reporting." },
      { t: "Compound", d: "Retention systems + creative pipeline that pay back inside 90 days." },
    ],
    outcomes: ["5–7x average ROAS", "20–40% CAC reduction", "30–60% less reporting overhead", "AI workflows owned by your team"],
    faqs: [
      { q: "How is this different from a growth marketer?", a: "Same fundamentals, but every engagement includes an AI-workflow layer — autonomous reporting, budget agents, creative-idea generation — so the team ships faster with less manual overhead." },
      { q: "What size company is this for?", a: "Best fit for startups and digital businesses spending $5K+/month on paid, or teams planning a Q2 launch." },
      { q: "What's the typical engagement?", a: "90-day sprint minimum. Week 1 audit, Week 2 tracking + LP fixes, Week 3 conversion optimisation, Week 4+ scaling and automation." },
    ],
  },
  "ai-marketing-consultant": {
    seoTitle: "AI Marketing Consultant · Autonomous Marketing Agents · Shivani Ranawat",
    seoDesc: "AI Marketing Consultant designing autonomous marketing agents, reporting bots and creative pipelines. Turn AI from a demo into a working system that compounds revenue.",
    eyebrow: "Consulting · AI Marketing",
    h1: "AI Marketing Consultant",
    lead: "Turn AI from a buzzword into a working growth system — autonomous agents, reporting bots and creative pipelines your team actually uses on Mondays.",
    problems: [
      "AI tools bought, no compounding output",
      "Team can't stitch tools into a workflow",
      "No system for tracking AI-driven lift",
      "Prompting isn't a product",
    ],
    approach: [
      { t: "Audit", d: "Current marketing stack + AI usage + measurable lift baseline." },
      { t: "Design", d: "3–5 autonomous or assisted workflows with retrieval + guardrails." },
      { t: "Ship", d: "Deploy on n8n / Make / OpenAI + light code where needed." },
      { t: "Measure", d: "90-day loop with clear lift metrics." },
    ],
    outcomes: ["30–60% less reporting overhead", "Faster budget decisions", "Compounding creative library", "AI workflows owned by team"],
    faqs: [
      { q: "Do we need engineers?", a: "No. I ship no-code + light-code stacks. If real code is needed, I bring it." },
      { q: "What's the signature build?", a: "An autonomous AI marketing agent that ingests ad + analytics data, detects issues, recommends budgets and emails a decision-ready daily report." },
    ],
  },
  "performance-marketing-consultant": {
    seoTitle: "Performance Marketing Consultant · Google, Meta, Amazon DSP · Shivani Ranawat",
    seoDesc: "Performance Marketing Consultant. Predictable, profitable paid acquisition across Google Ads, Meta Ads, Amazon DSP and native. 5–7x ROAS on budgets from $4K to $125K+ per campaign.",
    eyebrow: "Consulting · Performance",
    h1: "Performance Marketing Consultant",
    lead: "Predictable, profitable paid acquisition for D2C, OTT and B2B teams scaling past $10K/mo. Consistent 5–7x ROAS across Google, Meta, Amazon DSP and native.",
    problems: ["ROAS plateau", "Rising CAC", "Creative fatigue", "Attribution confusion"],
    approach: [
      { t: "Audit spend", d: "Find the leaks — targeting, creative, bidding, landing pages." },
      { t: "Rebuild", d: "Fresh targeting, creative-testing framework, and offer engineering." },
      { t: "Measure", d: "Full-funnel GA4 + platform + server-side attribution." },
      { t: "Scale", d: "Compound the winners, kill the losers weekly." },
    ],
    outcomes: ["5–7x average ROAS", "20–40% CAC reduction", "Compounding creative library"],
    faqs: [
      { q: "Which platforms?", a: "Google Ads, Meta Ads, Amazon DSP, DV360, Taboola, Outbrain, MGID." },
      { q: "Hands-on media buying?", a: "Yes — strategy + hands-on-keyboard through Aiadspace." },
    ],
  },
  "google-ads-consultant": {
    seoTitle: "Google Ads Consultant · High-Intent Acquisition · Shivani Ranawat",
    seoDesc: "Google Ads Consultant rebuilding accounts for high-intent, profitable acquisition. Enhanced Conversions, sGTM, Performance Max with guardrails. Pays back inside 90 days.",
    eyebrow: "Consulting · Google Ads",
    h1: "Google Ads Consultant",
    lead: "High-intent traffic that pays back inside 90 days. Account rebuilds, Enhanced Conversions, and Performance Max with guardrails.",
    problems: ["Wasted spend on broad match", "Poor SKAG hygiene", "No offline-conversion loop", "Performance Max as a black box"],
    approach: [
      { t: "Restructure", d: "Rebuild campaigns, ad groups, keywords with modern best practice." },
      { t: "Track", d: "Enhanced Conversions + CAPI + server-side GTM." },
      { t: "Deploy PMax", d: "With asset groups, exclusions and audience signals." },
      { t: "Iterate", d: "Weekly optimisation loop with clear lift attribution." },
    ],
    outcomes: ["Cleaner attribution", "Higher-quality traffic", "Lower CAC per SKU / service", "PMax that isn't a black box"],
    faqs: [
      { q: "YouTube ads?", a: "Yes — including creative testing and shorts-first strategies." },
      { q: "Do you do offline conversion imports?", a: "Yes. Critical for B2B and considered-purchase categories." },
    ],
  },
  "meta-ads-consultant": {
    seoTitle: "Meta Ads Consultant · Creative-Led D2C & OTT Growth · Shivani Ranawat",
    seoDesc: "Meta Ads Consultant for D2C and OTT growth. Creative testing frameworks, Advantage+ with disciplined signals, iOS attribution recovery and LTV/CAC optimisation.",
    eyebrow: "Consulting · Meta Ads",
    h1: "Meta Ads Consultant",
    lead: "Creative-led performance for D2C and OTT growth. Advantage+ with disciplined signals, weekly creative velocity, and a retention layer that lifts LTV.",
    problems: ["Creative fatigue", "Poor iOS attribution", "CAC creep in peak seasons", "Advantage+ misconfigured"],
    approach: [
      { t: "Creative velocity", d: "10+ variants a week under a tested brief framework." },
      { t: "Signals", d: "Clean CAPI + pixel + offline events for Advantage+." },
      { t: "Retention", d: "WhatsApp + email loops to lift LTV, not just first click." },
      { t: "Iterate", d: "Kill quickly, scale slowly, learn continuously." },
    ],
    outcomes: ["Fresh creative pipeline", "CAC discipline under peak-season pressure", "Better LTV/CAC"],
    faqs: [
      { q: "UGC and creators?", a: "Yes — briefing, sourcing and iteration under a live-testing framework." },
      { q: "Do you handle production?", a: "I brief and manage output. Production runs through vetted creators." },
    ],
  },
  "cro-consultant": {
    seoTitle: "CRO Consultant · Landing Page & Funnel Optimisation · Shivani Ranawat",
    seoDesc: "Conversion Rate Optimisation Consultant. 9-point CRO audit, ICE-scored backlog, A/B testing cadence and mobile UX rebuilds. 1.5–3x conversion rate lift.",
    eyebrow: "Consulting · CRO",
    h1: "Conversion Rate Optimisation Consultant",
    lead: "Fix the leak before you pour more traffic in. A 9-point CRO audit, an ICE-scored backlog, and a testing cadence that ships lift week over week.",
    problems: ["Low CVR", "High checkout drop-off", "Broken mobile UX", "Unclear value proposition"],
    approach: [
      { t: "Audit", d: "9-point CRO audit across nav, CTA, trust, mobile, checkout, copy, forms, analytics, heatmaps." },
      { t: "Backlog", d: "ICE-scored priorities you can execute without me." },
      { t: "Test", d: "A/B cadence + LP rebuilds. Ship one meaningful test a week." },
      { t: "Learn", d: "Insights library so wins compound instead of being one-offs." },
    ],
    outcomes: ["1.5–3x conversion rate", "Higher AOV via offer engineering", "Better mobile UX"],
    faqs: [
      { q: "How long?", a: "90–120 days minimum for statistically significant lift across multiple pages." },
      { q: "Which tools?", a: "GA4, Hotjar / Clarity, VWO / Convert, plus a copy + design partner." },
    ],
  },
  "marketing-automation-consultant": {
    seoTitle: "Marketing Automation Consultant · Email, WhatsApp, Lifecycle · Shivani Ranawat",
    seoDesc: "Marketing Automation Consultant designing email, WhatsApp and lifecycle systems that lift LTV. Klaviyo, Mailmodo, Wati, Interakt. 10–25% incremental revenue.",
    eyebrow: "Consulting · Automation",
    h1: "Marketing Automation Consultant",
    lead: "Email, WhatsApp and lifecycle systems that lift LTV. Segmentation, journeys per lifecycle stage, and automated dashboards so revenue keeps compounding.",
    problems: ["No lifecycle flows", "Broken abandonment recovery", "Weak segmentation", "Owned channels underused"],
    approach: [
      { t: "Schema", d: "Segment library + event schema tied to LTV." },
      { t: "Journeys", d: "Flows for onboarding, abandonment, winback, VIP." },
      { t: "Loops", d: "WhatsApp + email + retargeting stitched together." },
      { t: "Report", d: "Automated dashboards for owned-channel revenue." },
    ],
    outcomes: ["10–25% incremental revenue from owned channels", "Higher repeat rate", "Automated reporting"],
    faqs: [
      { q: "Which tools?", a: "Klaviyo, Mailmodo, Wati, Interakt — matched to stack + budget." },
      { q: "Do you build content?", a: "I brief and manage flow copy + design. Bring your own or use a partner." },
    ],
  },
};
